import React, { useState } from 'react';
import {
  X,
  Settings,
  Key,
  Globe,
  Sparkles,
  Check,
  AlertCircle,
  ExternalLink,
  RotateCw,
  Database,
  Cloud,
} from 'lucide-react';
import { AppSettings } from '../types';
import { api } from '../lib/api';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AppSettings | null;
  onSettingsSaved: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsSaved,
}) => {
  if (!isOpen) return null;

  const [geminiKey, setGeminiKey] = useState<string>('');
  const [preferredModel, setPreferredModel] = useState<string>(
    settings?.settings.preferred_model || 'gemini-2.5-flash'
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isTesting, setIsTesting] = useState<boolean>(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
    models?: string[];
  } | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const handleTestKey = async () => {
    setIsTesting(true);
    setTestResult(null);
    setErrorMsg('');

    try {
      const res = await api.settings.testGeminiKey(geminiKey || undefined);
      setTestResult({
        success: true,
        message: res.message,
        models: res.models_found,
      });
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err?.message || 'Failed to validate Google AI Studio API key.',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setErrorMsg('');

    try {
      await api.settings.save({
        gemini_api_key: geminiKey.trim() || undefined,
        preferred_model: preferredModel,
      });

      setSaveSuccess(true);
      onSettingsSaved();
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setErrorMsg(err?.message || 'Failed to save settings.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl w-full max-w-xl overflow-hidden shadow-2xl my-auto animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/50">
          <div className="flex items-center space-x-2">
            <div className="p-2 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">App Settings &amp; AI Setup</h3>
              <p className="text-xs text-slate-400">Configure Google AI Vision &amp; Cloudflare hosting</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {errorMsg && (
          <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-2 text-xs text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0 text-red-400" />
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleSave} className="p-6 space-y-6">
          {/* Section 1: Google AI API Key */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
                <Key className="w-3.5 h-3.5 text-brand-400" />
                <span>Google AI Studio API Key</span>
              </label>

              <a
                href="https://aistudio.google.com/app/apikey"
                target="_blank"
                rel="noreferrer"
                className="text-xs text-brand-400 hover:text-brand-300 font-semibold inline-flex items-center gap-1"
              >
                <span>Get Free Key</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

            <div className="relative">
              <input
                type="text"
                placeholder={settings?.has_gemini_key ? `Key configured (${settings.gemini_key_masked})` : 'Paste your Google AI API key'}
                value={geminiKey}
                onChange={(e) => setGeminiKey(e.target.value)}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] text-slate-400">
                {settings?.has_gemini_key
                  ? 'Active in database & Cloudflare environment'
                  : 'Key required for automatic Brand & Size detection and Try-On analysis.'}
              </span>

              <button
                type="button"
                onClick={handleTestKey}
                disabled={isTesting}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold border border-slate-700 transition-colors flex items-center gap-1.5 shrink-0"
              >
                {isTesting ? (
                  <RotateCw className="w-3 h-3 animate-spin" />
                ) : (
                  <Sparkles className="w-3 h-3 text-brand-400" />
                )}
                <span>Test Key</span>
              </button>
            </div>

            {/* Test result display */}
            {testResult && (
              <div
                className={`p-3 rounded-xl border text-xs flex flex-col gap-1 ${
                  testResult.success
                    ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                    : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                }`}
              >
                <div className="flex items-center gap-2 font-bold">
                  {testResult.success ? (
                    <Check className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-400" />
                  )}
                  <span>{testResult.message}</span>
                </div>
                {testResult.models && (
                  <div className="text-[11px] text-emerald-400/80 mt-1">
                    Available Models: {testResult.models.slice(0, 4).join(', ')}...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 2: Preferred Model */}
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-300 uppercase tracking-wider block">
              Default Vision &amp; Try-On AI Model
            </label>
            <select
              value={preferredModel}
              onChange={(e) => setPreferredModel(e.target.value)}
              className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-500/50"
            >
              <option value="gemini-2.5-flash">Gemini 2.5 Flash (Fastest &amp; Most Accurate)</option>
              <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
              <option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
            </select>
          </div>

          {/* Section 3: Cloudflare Hosting & Subdomain Info */}
          <div className="bg-slate-950/70 border border-slate-800 p-4 rounded-2xl space-y-3">
            <div className="flex items-center space-x-2 text-white font-bold text-xs">
              <Globe className="w-4 h-4 text-sky-400" />
              <span>Hosting: closet.tiger-home.com</span>
            </div>

            <div className="text-xs text-slate-400 space-y-1 leading-relaxed">
              <p>
                To bind this app to your domain on Cloudflare Pages:
              </p>
              <ol className="list-decimal list-inside space-y-1 text-slate-300 pt-1">
                <li>Create remote D1 database: <code className="text-brand-300">npm run db:init:remote</code></li>
                <li>Deploy to Cloudflare Pages: <code className="text-brand-300">npx wrangler pages deploy dist</code></li>
                <li>In Cloudflare Dashboard &rarr; <strong>Pages</strong> &rarr; <strong>Custom Domains</strong> &rarr; Add <code className="text-sky-300">closet.tiger-home.com</code></li>
              </ol>
            </div>
          </div>

          {/* Footer Save */}
          <div className="pt-2 flex items-center justify-between">
            {saveSuccess ? (
              <span className="text-xs font-bold text-emerald-400 flex items-center gap-1">
                <Check className="w-4 h-4" />
                <span>Settings saved!</span>
              </span>
            ) : <span />}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-slate-300 text-xs font-semibold"
              >
                Close
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className="px-5 py-2 rounded-xl bg-gradient-to-r from-brand-600 to-indigo-600 hover:from-brand-500 hover:to-indigo-500 text-white text-xs font-bold shadow-lg shadow-brand-600/30 transition-all"
              >
                {isSaving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
